/**
 * Data Item Sharing API client (M19).
 *
 * Per-user, view-only sharing of data items (My Files), parallel to the report access client.
 * Talks to the dash-api share endpoints:
 *   GET    /api/data-items/{id}/shares
 *   POST   /api/data-items/{id}/shares          { shareWithUserId }
 *   DELETE /api/data-items/{id}/shares/{sharedWithUserId}
 *
 * All three return an enriched DataItemShareResponse:
 *   { dataItemId, ownerUserId, owner: UserSummary, canShare, recipients: [ShareRecipient] }
 */

import {getSourcesApiBase} from '../../features/products/reportEditorPage/leftPanel/components/sourcesApi';
import {getAwpClientHeaders} from '../../session/awpRequestHeaders';

function extractErrorMessage(errorText, fallback) {
    if (!errorText) return fallback;
    try {
        const parsed = JSON.parse(errorText);
        return parsed?.message || parsed?.error || parsed?.detail || fallback;
    } catch (_) {
        return errorText || fallback;
    }
}

function requireArgs(dataItemId, userId) {
    if (dataItemId === null || dataItemId === undefined || dataItemId === '') {
        throw new Error('dataItemId is required');
    }
    if (!userId) {
        throw new Error('User ID is required');
    }
}

/** GET the enriched share state (owner + recipients) for a data item. Owner-only. */
export async function getDataItemShares({dataItemId, userId, signal} = {}) {
    requireArgs(dataItemId, userId);
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(dataItemId)}/shares?${query}`,
        {method: 'GET', headers: {...getAwpClientHeaders(), Accept: 'application/json', 'X-User-Id': userId}, signal},
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to load shares (${response.status})`));
    }
    return response.json();
}

/** Share a data item with another user (view-only). Returns the updated share state. */
export async function addDataItemShare({dataItemId, shareWithUserId, userId, signal} = {}) {
    requireArgs(dataItemId, userId);
    if (!shareWithUserId) {
        throw new Error('shareWithUserId is required');
    }
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(dataItemId)}/shares?${query}`,
        {
            method: 'POST',
            headers: {
                ...getAwpClientHeaders(),
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-User-Id': userId,
            },
            body: JSON.stringify({shareWithUserId}),
            signal,
        },
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to share (${response.status})`));
    }
    return response.json();
}

/** Revoke a user's access to a data item. Returns the updated share state. */
export async function removeDataItemShare({dataItemId, sharedWithUserId, userId, signal} = {}) {
    requireArgs(dataItemId, userId);
    if (!sharedWithUserId) {
        throw new Error('sharedWithUserId is required');
    }
    const baseUrl = getSourcesApiBase();
    const query = new URLSearchParams({userId}).toString();
    const response = await fetch(
        `${baseUrl}/api/data-items/${encodeURIComponent(dataItemId)}/shares/${encodeURIComponent(sharedWithUserId)}?${query}`,
        {method: 'DELETE', headers: {...getAwpClientHeaders(), Accept: 'application/json', 'X-User-Id': userId}, signal},
    );
    if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(extractErrorMessage(errorText, `Failed to revoke share (${response.status})`));
    }
    return response.json();
}
